import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';

import CommonRecipientsListComponent from 'webapp/components/common/recipients-list';
import CommonUiIcon from 'webapp/components/common/ui-icon';
import FormCheckboxSwitchComponent from 'webapp/components/form/checkbox-switch';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { Notification } from 'webapp/models-custom';

import type { ModelWithNotificationForPopup } from 'webapp/services/model-manager/notifications';

interface Args {
	model: Notification;
	selectedTargetObject: ModelWithNotificationForPopup;
	isDisabled: boolean;
	edit: (model: Notification) => void;
	delete: (model: Notification) => void;
}

export default class NotificationsEditListRowComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;

	get isSelected() {
		const thisTargetObject = this.args.model.targetObject,
			selectedTargetObject = this.args.selectedTargetObject;
		return (
			thisTargetObject.modelName === selectedTargetObject.modelName &&
			thisTargetObject.id === selectedTargetObject.id
		);
	}

	get isActiveSwitchDisabled() {
		return this.accountService.isDemoAccount || this.args.isDisabled;
	}

	@action
	toggle() {
		this.args.model.toggle();
	}

	@action
	edit() {
		if (this.args.isDisabled) {
			return;
		}
		this.args.edit(this.args.model);
	}

	@action
	delete() {
		if (this.args.isDisabled) {
			return;
		}
		this.args.delete(this.args.model);
	}

	<template>
		<tr class={{if this.isSelected "checked"}}>
			<td>
				<strong>
					{{@model.frequencyText}}
					<ToolTipComponent
						@text={{@model.frequencyTooltipText}}
						@position="top"
						@maxWidth={{230}}
					/>
				</strong>
				<br />
				<span>{{@model.scheduleTypeText}}</span>
			</td>
			<td class="prio-low">
				{{#each @model.deliveryTypesIcons as |iconData|}}
					<span>
						{{#if iconData.isStrokeIcon}}
							<CommonUiIcon
								@name={{iconData.icon}}
								@size="md"
								@stroke="currentColor"
								@fill="none"
							/>
						{{else}}
							<CommonUiIcon @name={{iconData.icon}} @size="md" />
						{{/if}}
						<ToolTipComponent @text={{iconData.text}} @position="top" />
					</span>
				{{/each}}
			</td>
			<td class="prio-low">
				<CommonRecipientsListComponent
					@recipients={{@model.allRecipients}}
					@activeFilter={{@model.activeFilter}}
				/>
			</td>
			<td class="prio-medium no-grow">
				{{#if @model.canToggle}}
					<span>
						{{#if @model.isToggleOn}}
							<ToolTipComponent
								@text="Disabling will turn off delivery for you and will not affect other recipients."
								@maxWidth={{230}}
							/>
						{{/if}}

						<FormCheckboxSwitchComponent
							@name="isActive"
							@selected={{@model.isToggleOn}}
							@onChange={{this.toggle}}
							@isDisabled={{this.isActiveSwitchDisabled}}
							@size="small"
						/>
					</span>
				{{/if}}
			</td>
			<td class="no-grow">
				<div class="actions">
					<a class="edit" {{on "click" this.edit}}>
						<CommonUiIcon @name="Pencil" @size="md" />
					</a>
					<a class="delete" {{on "click" this.delete}}>
						<CommonUiIcon @name="Trash" @size="md" />
					</a>
				</div>
			</td>
		</tr>
	</template>
}
